import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Star, ShoppingCart } from 'lucide-react';
import { trackFood, trackError } from '../Utils/analytics';
import OrderCreation from '../OrderCreation/OrderCreation';
import '../FeaturedMeals/FeaturedMeals.css';

interface Meal {
  id: number;
  food_name: string;
  description: string;
  price: number;
  category: string;
  phone_number: string;
  pickup_location: string;
  serving_size: string;
  preparation_time: string;
  available_quantity: number;
  image_urls: string[];
  user_id: number;
}

const FeaturedMeals: React.FC = () => {
  const [orders, setOrders] = useState<string[]>([]);
  const [onlineKitchensData, setOnlineKitchensData] = useState<any[]>([]);
  const [meals, setMeals] = useState<Meal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOrderCreationModal, setShowOrderCreationModal] = useState(false);
  const [selectedFoodItem, setSelectedFoodItem] = useState<Meal | null>(null);

  useEffect(() => {
    const fetchMeals = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const authToken = localStorage.getItem('authToken');
        
        if (!authToken) {
          setError('Authentication required. Please log in to view meals.');
          setIsLoading(false);
          return;
        }
        
        console.log('Fetching online kitchens with food items...');
        
        // Use the new endpoint that only returns food from online kitchens
        const response = await fetch('https://api.homecookt.com/api/v1/onlinekitchens-with-food', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        console.log('Online kitchens response status:', response.status);
        
        if (!response.ok) {
          if (response.status === 401) {
            setError('Your session has expired. Please log in again.');
          } else if (response.status === 403) {
            setError('You do not have permission to view these meals.');
          } else if (response.status === 404) {
            setError('No online kitchens with food items found at the moment.');
          } else {
            setError('Failed to load online kitchens. Please try again later.');
          }
          setIsLoading(false);
          return;
        }
        
        const data = await response.json();
        console.log('Online kitchens data:', data);
        
        // Store the full response for debugging
        setOnlineKitchensData(data);
        
        // Extract all food items from online kitchens
        const allMeals: Meal[] = [];
        
        if (Array.isArray(data)) {
          data.forEach((kitchenWithFood: any) => {
            if (kitchenWithFood.food_items && Array.isArray(kitchenWithFood.food_items)) {
              // Explicitly map food_id to id and add kitchen info to each food item
              const mealsWithKitchenInfo = kitchenWithFood.food_items.map((item: any) => ({
                id: item.food_id, // Explicitly map food_id to id
                food_name: item.food_name,
                description: item.description,
                price: item.price,
                category: item.category,
                phone_number: item.phone_number,
                pickup_location: item.pickup_location,
                serving_size: item.serving_size,
                preparation_time: item.preparation_time,
                available_quantity: item.available_quantity,
                image_urls: item.image_urls,
                user_id: item.user_id,
                kitchen_name: kitchenWithFood.kitchen?.kitchen_name || 'Unknown Kitchen',
                kitchen_id: kitchenWithFood.kitchen?.id || null
              }));
              allMeals.push(...mealsWithKitchenInfo);
            }
          });
        }
        
        console.log('Extracted meals from online kitchens:', allMeals);
        setMeals(allMeals);
        
        if (allMeals.length === 0) {
          setError('No food items available from online kitchens at the moment. Check back later!');
        }
        
      } catch (error) {
        console.error('Error fetching meals:', error);
        setError('Network error. Please check your connection and try again.');
        trackError('featured_meals_fetch_failed', 'featured_meals');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMeals();
    
    // Listen for kitchen status changes to refresh meals
    const handleKitchenStatusChange = () => {
      console.log('Kitchen status changed, refreshing meals...');
      fetchMeals();
    };
    
    const handleKitchenDeleted = () => {
      console.log('Kitchen deleted, refreshing meals...');
      fetchMeals();
    };
    
    const handleKitchenCreated = () => {
      console.log('Kitchen created, refreshing meals...');
      fetchMeals();
    };
    
    const handleFoodItemCreated = () => {
      console.log('Food item created, refreshing meals...');
      fetchMeals();
    };
    
    window.addEventListener('kitchenStatusChanged', handleKitchenStatusChange);
    window.addEventListener('kitchenDeleted', handleKitchenDeleted);
    window.addEventListener('kitchenCreated', handleKitchenCreated);
    window.addEventListener('foodItemCreated', handleFoodItemCreated);
    
    return () => {
      window.removeEventListener('kitchenStatusChanged', handleKitchenStatusChange);
      window.removeEventListener('kitchenDeleted', handleKitchenDeleted);
      window.removeEventListener('kitchenCreated', handleKitchenCreated);
      window.removeEventListener('foodItemCreated', handleFoodItemCreated);
    };
  }, []);

  const handleOrderFood = (mealId: number) => {
    const meal = meals.find(m => m.id === mealId);
    if (meal) {
      console.log('FeaturedMeals: Selected meal data:', meal);
      console.log('FeaturedMeals: Price being passed to OrderCreation:', meal.price);
      console.log('FeaturedMeals: Meal ID:', meal.id, 'Food Name:', meal.food_name);
      setSelectedFoodItem(meal);
      setShowOrderCreationModal(true);
    } else {
      alert('Food item not found. Please try again.');
      trackError('food_item_not_found', 'featured_meals');
    }
  };

  const handlePlaceOrderWithAddress = async (foodItemId: number, deliveryAddress: string, quantity: number) => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new Error('Authentication required. Please log in to place an order.');
      }

      // Create FormData for multipart/form-data request
      const formData = new FormData();
      
      // Create order data object and send as JSON string
      const orderData = {
        food_item_id: foodItemId,
        quantity: quantity,
        delivery_address: deliveryAddress
      };
      
      // Append order data as JSON string
      formData.append('order', JSON.stringify(orderData));
      
      // Add payment method types - append each method separately
      const paymentMethods = ['card', 'upi'];
      paymentMethods.forEach(method => {
        formData.append('payment_method_types', method);
      });

      console.log('Placing order with data:', {
        order: orderData,
        payment_method_types: paymentMethods
      });

      const response = await fetch('https://api.homecookt.com/api/v1/order', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        // Order placed successfully
        const orderId = data.id;
        setOrders(prev => [...prev, orderId.toString()]);
        
        // Show success message
        alert(`Order placed successfully! Order ID: ${orderId}`);
        
        // Close the modal
        setShowOrderCreationModal(false);
        setSelectedFoodItem(null);
        
        trackFood('food_ordered', selectedFoodItem?.food_name || 'unknown');
      } else {
        // Handle API errors
        if (response.status === 422) {
          throw new Error(data.detail?.[0]?.msg || 'Validation error. Please check your order details.');
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 404) {
          throw new Error('Food item not found or no longer available.');
        } else if (response.status === 400) {
          throw new Error(data.message || 'Invalid order details. Please check your information.');
        } else {
          throw new Error(data.message || 'Failed to place order. Please try again.');
        }
      }
    } catch (error: any) {
      console.error('Order placement error:', error);
      trackError('food_order_api_failed', 'featured_meals');
      throw error; // Re-throw to be handled by OrderCreation component
    }
  };

  const handleCloseOrderModal = () => {
    setShowOrderCreationModal(false);
    setSelectedFoodItem(null);
  };

  return (
    <section className="featured-meals">
      <div className="meals-header">
        <h2 className="featured-meals-title">Featured Meals Near You</h2>
        <p className="meals-subtitle">Fresh, homemade food delivered to your door</p>
        {isLoading && (
          <div className="meals-loading">
            <div className="loading-spinner"></div>
            <p>Loading meals...</p>
          </div>
        )}
        {error && (
          <div className="meals-error">
            <p>{error}</p>
          </div>
        )}
      </div>

      {/* Active Orders */}
      {orders.length > 0 && (
        <div className="active-orders">
          <h3>Your Active Orders</h3>
          <div className="orders-list">
            {orders.map((orderId, index) => (
              <div key={orderId} className="order-item">
                <div className="order-info">
                  <span className="order-id">Order #{orderId.slice(-6)}</span>
                  <span className="order-status">Preparing</span>
                </div>
                <div className="order-status-text">
                  Your order is being prepared
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order Creation Modal */}
      {showOrderCreationModal && selectedFoodItem && (
        <OrderCreation
          foodItemId={selectedFoodItem.id}
          foodItemName={selectedFoodItem.food_name}
          foodItemPrice={selectedFoodItem.price}
          onClose={handleCloseOrderModal}
          onOrderSubmit={handlePlaceOrderWithAddress}
        />
      )}
      
      {!isLoading && !error && (
        <div className="meals-grid">
          {meals.length === 0 ? (
            <div className="no-meals-message">
              <p>No meals available at the moment. Check back soon!</p>
            </div>
          ) : (
            meals.map(meal => (
              <div key={meal.id} className="meal-card">
                <div className="meal-image-container">
                  {(() => {
                    // Debug logging
                    console.log('Meal:', meal.food_name);
                    console.log('Image URLs:', meal.image_urls);
                    console.log('First image URL:', meal.image_urls?.[0]);
                    
                    const imageUrl = meal.image_urls && meal.image_urls.length > 0 
                      ? meal.image_urls[0] 
                      : "https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg";
                    
                    console.log('Final image URL:', imageUrl);
                    
                    return (
                      <img 
                        src={imageUrl}
                        alt={meal.food_name} 
                        className="meal-image"
                        onLoad={() => {
                          console.log('Image loaded successfully:', imageUrl);
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          console.log('Image failed to load:', target.src);
                          console.log('Setting fallback image');
                          target.src = "https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg";
                        }}
                      />
                    );
                  })()}
                  <div className="meal-badges">
                    <span className="rating-badge">
                      <Star size={12} fill="currentColor" />
                      4.8
                    </span>
                  </div>
                </div>
                
                <div className="meal-content">
                  <div className="meal-header">
                    <h3 className="meal-title">{meal.food_name}</h3>
                    <p className="meal-chef">by {(meal as any).kitchen_name || `Chef #${meal.user_id}`}</p>
                  </div>
    
                  <div className="meal-meta">
                    <div className="meta-item">
                      <MapPin size={14} />
                     <span>{meal.pickup_location && meal.pickup_location.length > 15 ? `${meal.pickup_location.substring(0, 15)}...` : meal.pickup_location}</span>
                    </div>
                    <div className="meta-item">
                      <Clock size={14} />
                      <span>{meal.preparation_time}</span>
                    </div>
                  </div>
    
                  <div className="meal-pricing">
                    <div className="price-info">
                     <span className="meal-price">₹{typeof meal.price === 'number' ? meal.price.toFixed(2) : meal.price}</span>
                     <span className="delivery-fee">Serves: {meal.serving_size || 'N/A'}</span>
                    </div>
                    <div className="meal-actions">
                      <button 
                        onClick={() => handleOrderFood(meal.id)}
                        className="order-btn"
                      >
                        <ShoppingCart size={16} />
                        Order Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div className="delivery-info">
        <div className="info-card">
          <h4>🚚 Fast Delivery</h4>
          <p>Average delivery time: 25-35 minutes</p>
        </div>
        <div className="info-card">
          <h4>👨‍🍳 Online Kitchens</h4>
          <p>Fresh meals from active kitchens in your area</p>
        </div>
        <div className="info-card">
          <h4>📱 Easy Ordering</h4>
          <p>Simple ordering process with secure payments</p>
        </div>
      </div>
    </section>
  );
};

export default FeaturedMeals;